import React from 'react';
import {
  Box,
  Typography,
  Button,
  Stack,
  SvgIcon,
  Paper,
  alpha,
} from '@mui/material';
import {
  SearchOff,
  FilterAltOff,
  Inventory2Outlined,
  TrendingUpOutlined,
  AssessmentOutlined,
  StorefrontOutlined,
  CategoryOutlined,
  LocalShippingOutlined,
  ErrorOutlineOutlined,
  InfoOutlined,
} from '@mui/icons-material';

export type EmptyStateType =
  | 'no-data'
  | 'no-results'
  | 'no-filters'
  | 'error'
  | 'no-inventory'
  | 'no-sales'
  | 'no-stores'
  | 'no-products'
  | 'no-orders'
  | 'custom';

interface EmptyStateConfig {
  icon: typeof SvgIcon;
  title: string;
  description: string;
  actionLabel?: string;
  secondaryActionLabel?: string;
  showSuggestions?: boolean;
}

const emptyStateConfigs: Record<EmptyStateType, EmptyStateConfig> = {
  'no-data': {
    icon: InfoOutlined,
    title: 'No Data Available',
    description: 'There is no data to display at the moment. Try adjusting your date range or filters.',
    actionLabel: 'Clear All Filters',
    showSuggestions: true,
  },
  'no-results': {
    icon: SearchOff,
    title: 'No Results Found',
    description: 'We couldn\'t find any data matching your search criteria.',
    actionLabel: 'Clear Search',
    secondaryActionLabel: 'Adjust Filters',
    showSuggestions: true,
  },
  'no-filters': {
    icon: FilterAltOff,
    title: 'No Matching Data',
    description: 'Your current filters don\'t match any available data.',
    actionLabel: 'Reset Filters',
    showSuggestions: true,
  },
  'error': {
    icon: ErrorOutlineOutlined,
    title: 'Something Went Wrong',
    description: 'We encountered an error while loading your data. Please try again.',
    actionLabel: 'Retry',
    secondaryActionLabel: 'Contact Support',
  },
  'no-inventory': {
    icon: Inventory2Outlined,
    title: 'No Inventory Data',
    description: 'No inventory records found for the selected period.',
    actionLabel: 'Import Inventory',
    showSuggestions: true,
  },
  'no-sales': {
    icon: TrendingUpOutlined,
    title: 'No Sales Data',
    description: 'No sales transactions found for the selected period.',
    actionLabel: 'Import Sales Data',
    showSuggestions: true,
  },
  'no-stores': {
    icon: StorefrontOutlined,
    title: 'No Stores Found',
    description: 'No stores match your current selection.',
    actionLabel: 'View All Stores',
  },
  'no-products': {
    icon: CategoryOutlined,
    title: 'No Products Found',
    description: 'No products match your current criteria.',
    actionLabel: 'View All Products',
  },
  'no-orders': {
    icon: LocalShippingOutlined,
    title: 'No Orders Found',
    description: 'No replenishment orders for the selected period.',
    actionLabel: 'Create Order',
  },
  'custom': {
    icon: InfoOutlined,
    title: 'No Data',
    description: 'No information available.',
  },
};

interface EmptyStateProps {
  type?: EmptyStateType;
  title?: string;
  description?: string;
  icon?: typeof SvgIcon;
  actionLabel?: string;
  onAction?: () => void;
  secondaryActionLabel?: string;
  onSecondaryAction?: () => void;
  suggestions?: string[];
  size?: 'small' | 'medium' | 'large';
  variant?: 'default' | 'paper';
}

export const EmptyState: React.FC<EmptyStateProps> = ({
  type = 'no-data',
  title,
  description,
  icon,
  actionLabel,
  onAction,
  secondaryActionLabel,
  onSecondaryAction,
  suggestions,
  size = 'medium',
  variant = 'default',
}) => {
  const config = emptyStateConfigs[type];
  const Icon = icon || config.icon;
  const displayTitle = title || config.title;
  const displayDescription = description || config.description;
  const displayActionLabel = actionLabel || config.actionLabel;
  const displaySecondaryActionLabel = secondaryActionLabel || config.secondaryActionLabel;

  const iconSize = size === 'small' ? 60 : size === 'large' ? 120 : 80;
  const spacing = size === 'small' ? 2 : size === 'large' ? 4 : 3;

  const defaultSuggestions = [
    'Try selecting a different date range',
    'Remove some filters to see more results',
    'Check if data has been imported for this period',
    'Contact support if the issue persists',
  ];

  const displaySuggestions = suggestions || (config.showSuggestions ? defaultSuggestions.slice(0, 3) : []);

  const content = (
    <Stack
      spacing={spacing}
      alignItems="center"
      sx={{
        py: spacing * 2,
        px: 3,
        textAlign: 'center',
        maxWidth: 600,
        mx: 'auto',
      }}
    >
      <Box
        sx={{
          width: iconSize,
          height: iconSize,
          borderRadius: '50%',
          bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Icon
          sx={{
            fontSize: iconSize * 0.6,
            color: 'primary.main',
            opacity: 0.6,
          }}
        />
      </Box>

      <Stack spacing={1}>
        <Typography
          variant={size === 'small' ? 'h6' : size === 'large' ? 'h4' : 'h5'}
          color="text.primary"
          fontWeight="medium"
        >
          {displayTitle}
        </Typography>
        <Typography
          variant={size === 'small' ? 'body2' : 'body1'}
          color="text.secondary"
        >
          {displayDescription}
        </Typography>
      </Stack>

      {displaySuggestions.length > 0 && (
        <Box
          sx={{
            bgcolor: (theme) => alpha(theme.palette.info.main, 0.08),
            borderRadius: 2,
            p: 2,
            width: '100%',
          }}
        >
          <Typography variant="subtitle2" color="info.main" gutterBottom>
            Suggestions:
          </Typography>
          <Stack spacing={0.5}>
            {displaySuggestions.map((suggestion, index) => (
              <Typography
                key={index}
                variant="caption"
                color="text.secondary"
                sx={{ display: 'flex', alignItems: 'flex-start' }}
              >
                • {suggestion}
              </Typography>
            ))}
          </Stack>
        </Box>
      )}

      {(displayActionLabel || displaySecondaryActionLabel) && (
        <Stack direction="row" spacing={2}>
          {displayActionLabel && onAction && (
            <Button
              variant="contained"
              onClick={onAction}
              size={size === 'small' ? 'small' : 'medium'}
            >
              {displayActionLabel}
            </Button>
          )}
          {displaySecondaryActionLabel && onSecondaryAction && (
            <Button
              variant="outlined"
              onClick={onSecondaryAction}
              size={size === 'small' ? 'small' : 'medium'}
            >
              {displaySecondaryActionLabel}
            </Button>
          )}
        </Stack>
      )}
    </Stack>
  );

  if (variant === 'paper') {
    return (
      <Paper elevation={0} sx={{ bgcolor: 'background.default' }}>
        {content}
      </Paper>
    );
  }

  return content;
};

/**
 * Quick empty state for inline use in smaller components
 */
export const InlineEmptyState: React.FC<{
  message: string;
  action?: { label: string; onClick: () => void };
}> = ({ message, action }) => (
  <Box
    sx={{
      textAlign: 'center',
      py: 3,
      px: 2,
      bgcolor: (theme) => alpha(theme.palette.grey[100], 0.5),
      borderRadius: 1,
    }}
  >
    <Typography variant="body2" color="text.secondary" gutterBottom>
      {message}
    </Typography>
    {action && (
      <Button size="small" onClick={action.onClick} sx={{ mt: 1 }}>
        {action.label}
      </Button>
    )}
  </Box>
);